import type {
  BalancePostingWrapper,
  BalancePostingWrapperCollection,
} from '@festina/life-general-types/life'
import {
  dateUtils,
  notEmpty,
  type Optional,
  translationUtils,
} from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { useMemo } from 'react'

import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { POSTING_DIMENSIONS } from '../../utility/postingUtils'

type Props = {
  balancePostings: Optional<BalancePostingWrapperCollection>
  onWorkPadFilterSelected?: (workPadId: Optional<string>) => void
  shouldHideAccount?: boolean
}

const defineColumn = createColumnDefiner<BalancePostingWrapper>()

export const BalancePostingsTable = ({
  balancePostings,
  onWorkPadFilterSelected,
  shouldHideAccount,
}: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { formatWithDecimals } = useAmountFormatter()

  const balancePostingWrappers = useMemo(
    () => balancePostings?.balancePostingWrappers?.filter(notEmpty) ?? [],
    [balancePostings],
  )

  const dimsToShow = useMemo(() => {
    const dimsToShow: Record<string, true | undefined> = {}
    balancePostingWrappers.forEach((posting) =>
      POSTING_DIMENSIONS.forEach((dim) => {
        if (notEmpty(posting?.balancePosting?.dimensions?.[dim])) {
          dimsToShow[dim] = true
        }
      }),
    )

    return dimsToShow
  }, [balancePostingWrappers])

  const columns = useMemo(
    () => ({
      seqno: defineColumn({
        heading: translate('postings.column.seqno'),
        keyPath: 'balancePosting.seqno',
        sortOptions: { sortType: 'desc' },
      }),
      transactionTime: defineColumn({
        heading: translate('postings.column.transactionTime'),
        keyPath: 'balancePosting.transactionTime',
        formatter: dateUtils.dateAsStr,
      }),
      valueDate: defineColumn({
        heading: translate('postings.column.valueDate'),
        keyPath: 'balancePosting.valueDate',
        formatter: dateUtils.dateAsStr,
      }),
      ...(shouldHideAccount
        ? {}
        : {
            accountType: defineColumn({
              heading: translate('postings.column.accountType'),
              keyPath: 'balancePosting.accountType',
              formatter: (x) => translateEnum('life.enum.AccountType', x),
            }),
          }),
      postingType: defineColumn({
        heading: translate('postings.column.postingType'),
        keyPath: 'balancePosting.postingType',
        formatter: (x) => translateEnum('life.enum.PostingType', x),
      }),
      amount: defineColumn({
        heading: translate('postings.column.amount'),
        keyPath: 'balancePosting.amount',
        formatter: formatWithDecimals,
      }),
      ...Object.fromEntries(
        POSTING_DIMENSIONS.filter((dim) => dimsToShow[dim]).map((dim) => [
          dim,
          defineColumn({
            heading: translateEnum('enum.PostingDimensions', dim),
            keyPath: `balancePosting.dimensions.${dim}`,
          }),
        ]),
      ),
      truncated: defineColumn({
        heading: translate('postings.column.truncated'),
        keyPath: 'balancePosting.truncated',
        formatter: (x) => translationUtils.translateBoolean(x, translate),
      }),
      ...(onWorkPadFilterSelected
        ? {
            workPadRef: defineColumn({
              heading: translate('postings.column.workPadRef'),
              keyPath: 'balancePosting.workPadRef._id',
              formatter: (workPadId) => (
                <IconButton
                  tip={translate('rowAction.view')}
                  iconName="DocumentText"
                  iconSize="md"
                  onClick={() => onWorkPadFilterSelected(workPadId)}
                />
              ),
            }),
          }
        : {}),
    }),
    [
      dimsToShow,
      formatWithDecimals,
      onWorkPadFilterSelected,
      shouldHideAccount,
      translate,
      translateEnum,
    ],
  )

  return <SortableTable data={balancePostingWrappers} columns={columns} />
}
